// pages/CreditEnginePage.jsx
import React, { useEffect, useState } from "react";
import {
  Button,
  TextInput,
  Modal,
  Alert,
  Badge,
  Spinner,
} from "flowbite-react";
import {
  HiOutlineSparkles,
  HiOutlineShieldCheck,
  HiOutlineCog,
  HiOutlineInformationCircle,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiThumbDown,
  HiThumbUp,
  HiSearch,
} from "react-icons/hi";
import axiosInstance from "../config/axiosInstance";
import CreditEngineSettingsModal from "../components/modals/CreditEngineSettingsModal";
import { useApplication } from "../contexts/ApplicationContext";
import { isValidCNPJ, isValidCPF } from "../utils/utils";
import { ADMIN, QUERIES, USERS } from "../config/apiConfig";
import "./CreditEnginePage.css";
import { useQuery } from "react-query";
import ForbiddenQueriesPermission from "../components/common/ForbiddenQueriesPermission";

const cx = (...c) => c.filter(Boolean).join(" ");

// ======================
// Helpers gerais
// ======================
const onlyDigits = (v = "") => v.replace(/\D/g, "");

// 000.000.000-00 ou 00.000.000/0000-00
function formatCpfCnpj(v = "") {
  const digits = onlyDigits(v);

  if (digits.length <= 11) {
    // CPF
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4")
      .slice(0, 14);
  }

  // CNPJ
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5")
    .slice(0, 18);
}

function validateCpfCnpj(value) {
  const digits = onlyDigits(value);
  if (!digits) return { ok: false, message: "Informe um CPF ou CNPJ." };

  if (digits.length === 11 && isValidCPF(digits)) {
    return { ok: true, type: "CPF" };
  }

  if (digits.length === 14 && isValidCNPJ(digits)) {
    return { ok: true, type: "CNPJ" };
  }

  return { ok: false, message: "CPF/CNPJ inválido. Verifique os dados." };
}

const moneyFmt = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});
const formatMoney = (v) => {
  const num = Number(v);
  if (!num) return null;
  return moneyFmt.format(num);
};

const formatDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR");
};

// ======================
// Helpers visuais de risco
// ======================
const RISK_CONFIG = {
  BAIXO: {
    label: "Risco Baixo",
    badgeClass: "cep-risk-badge cep-risk-badge-low",
  },
  MÉDIO: {
    label: "Risco Médio",
    badgeClass: "cep-risk-badge cep-risk-badge-medium",
  },
  ALTO: {
    label: "Risco Alto",
    badgeClass: "cep-risk-badge cep-risk-badge-high",
  },
  ALTÍSSIMO: {
    label: "Risco Altíssimo",
    badgeClass: "cep-risk-badge cep-risk-badge-very-high",
  },
};

// mapeia código de regra -> label legível
const RULE_LABELS = {
  PROTESTO_VALOR_ACIMA_MINIMO: "Protestos acima do valor mínimo configurado",
  CHEQUES_SEM_FUNDO_EXISTENTE: "Existência de cheques sem fundo",
  PENDENCIAS_ACIMA_MINIMO: "Pendências acima do valor mínimo configurado",
  SCORE_ABAIXO_THRESHOLD: "Score abaixo do limiar configurado",
};

// ======================
// Paleta para o gauge de score por classificação
// ======================
const SCORE_COLOR = {
  BAIXO: "#10b981", // emerald
  MÉDIO: "#f59e0b", // amber
  ALTO: "#f97316", // orange
  ALTÍSSIMO: "#ef4444", // red
};

// ======================
// Skeleton para loading da análise
// ======================
function SkeletonLine({ width = "100%", height = 10, className }) {
  return (
    <div
      className={cx("cep-skeleton-line", className)}
      style={{ width, height }}
    />
  );
}

function AnalysisResultSkeleton() {
  return (
    <div className="cep-analysis-card cep-analysis-card--skeleton">
      <div className="cep-analysis-header">
        <div className="cep-analysis-header-icon cep-analysis-header-icon--pulse" />
        <SkeletonLine width="160px" height={16} />
      </div>

      <div className="cep-analysis-main">
        <div className="cep-analysis-main-left">
          <div className="cep-gauge-wrapper cep-gauge-wrapper--skeleton">
            <div className="cep-gauge-skeleton-outer" />
            <div className="cep-gauge-skeleton-inner" />
          </div>
          <div className="cep-analysis-main-text">
            <SkeletonLine width="130px" height={14} />
            <SkeletonLine width="220px" />
            <div className="cep-chip-row">
              <SkeletonLine width="110px" height={20} />
              <SkeletonLine width="160px" height={20} />
            </div>
          </div>
        </div>
        <div className="cep-analysis-main-right">
          <SkeletonLine width="70%" height={14} />
          <SkeletonLine width="100%" />
          <SkeletonLine width="80%" />
        </div>
      </div>

      <div className="cep-analysis-footer">
        <div className="cep-analysis-col cep-analysis-col--positive">
          <SkeletonLine width="60%" />
          <SkeletonLine width="100%" />
          <SkeletonLine width="80%" />
        </div>
        <div className="cep-analysis-col cep-analysis-col--negative">
          <SkeletonLine width="60%" />
          <SkeletonLine width="100%" />
          <SkeletonLine width="75%" />
        </div>
      </div>
      <div className="cep-analysis-col cep-analysis-col--rules">
        <SkeletonLine width="70%" />
        <div className="cep-rule-chips-skeleton">
          <SkeletonLine width="40%" height={18} />
          <SkeletonLine width="50%" height={18} />
          <SkeletonLine width="30%" height={18} />
        </div>
      </div>
    </div>
  );
}

// ======================
// Card de resultado da análise
// ======================
function AnalysisResultCard({ result, onOpenDetails }) {
  if (!result) return null;

  const {
    statusRetorno,
    scoreFinal,
    classificacaoRisco,
    autoReprovadoPeloMotor,
    regrasMotorAcionadas = [],
    observacoesPositivas = [],
    observacoesNegativas = [],
    recomendacao,
    providerId,
    prioridade,
  } = result;

  if (statusRetorno !== 1) {
    return (
      <div className="cep-error-block">
        <div className="cep-error-title">
          <HiOutlineXCircle className="cep-error-icon" />
          <span>Não foi possível obter o resultado do motor.</span>
        </div>
        <p>Verifique a configuração do motor ou tente novamente.</p>
      </div>
    );
  }

  const inferTypeFromText = (text = "") => {
    const t = text.toLowerCase();

    if (t.includes("protesto")) return "PROTESTOS";
    if (t.includes("pendênc") || t.includes("pendenc")) return "PENDENCIAS";
    if (t.includes("cheque")) return "CHEQUES";
    if (t.includes("score")) return "SCORE";

    return "GENERIC";
  };

  const riskCfg = RISK_CONFIG[classificacaoRisco] || RISK_CONFIG["MÉDIO"] || {};

  const isNegar = recomendacao === "NEGAR";
  const isAprovarComRestricoes = recomendacao === "APROVAR_COM_RESTRICOES";
  const isAprovar = recomendacao === "APROVAR";

  const recomendacaoLabel =
    recomendacao === "NEGAR"
      ? "Negar crédito"
      : recomendacao === "APROVAR_COM_RESTRICOES"
      ? "Aprovar com restrições"
      : recomendacao === "APROVAR"
      ? "Aprovar crédito"
      : recomendacao || "Análise não concluída";

  const recomendacaoClass = isNegar
    ? "cep-recommendation--deny"
    : isAprovarComRestricoes
    ? "cep-recommendation--restrict"
    : isAprovar
    ? "cep-recommendation--approve"
    : "cep-recommendation--neutral";

  const scoreValue =
    typeof scoreFinal === "number"
      ? Math.max(0, Math.min(1000, scoreFinal))
      : null;
  const scorePercent = scoreValue != null ? (scoreValue / 1000) * 100 : 0;
  const gaugeColor =
    SCORE_COLOR[classificacaoRisco] || SCORE_COLOR["MÉDIO"] || "#6366f1";

  // pequeno mapa de ícones por regra
  const RULE_ICON_MAP = {
    PROTESTO_VALOR_ACIMA_MINIMO: HiOutlineInformationCircle,
    CHEQUES_SEM_FUNDO_EXISTENTE: HiOutlineXCircle,
    PENDENCIAS_ACIMA_MINIMO: HiOutlineXCircle,
    SCORE_ABAIXO_THRESHOLD: HiOutlineShieldCheck,
  };

  return (
    <div className="cep-analysis-card">
      {/* título do bloco */}
      <div className="cep-analysis-header">
        <div className="cep-analysis-header-icon">
          <HiOutlineShieldCheck className="cep-analysis-header-icon-svg" />
        </div>
        <div className="cep-analysis-header-text">
          <p className="cep-analysis-header-title">
            Parecer de crédito do motor
          </p>
          <p className="cep-analysis-header-subtitle">
            Resultado automático com base nas regras configuradas e nas
            informações de crédito do cliente.
          </p>
        </div>
      </div>

      {/* header score + risco + meta + recomendação */}
      <div className="cep-analysis-main">
        {/* recomendação final em destaque */}
        <div className={cx("cep-analysis-main-right", recomendacaoClass)}>
          <div className="cep-recommendation-header">
            {isNegar && (
              <div className="cep-recommendation-icon-wrapper">
                <HiThumbDown className="cep-recommendation-icon" />
              </div>
            )}
            {isAprovar && (
              <div className="cep-recommendation-icon-wrapper">
                <HiThumbUp className="cep-recommendation-icon" />
              </div>
            )}
            {isAprovarComRestricoes && (
              <div className="cep-recommendation-icon-wrapper">
                <HiOutlineInformationCircle className="cep-recommendation-icon" />
              </div>
            )}
            {!isNegar && !isAprovar && !isAprovarComRestricoes && (
              <div className="cep-recommendation-icon-wrapper">
                <HiOutlineInformationCircle className="cep-recommendation-icon" />
              </div>
            )}

            <div className="cep-recommendation-text">
              <span className="cep-recommendation-label">
                Recomendação do motor
              </span>
              <span className="cep-recommendation-title">
                {recomendacaoLabel}
              </span>
            </div>
          </div>

          <p className="cep-recommendation-description">
            Utilize este parecer como referência principal na decisão de
            crédito, combinado com a política interna e o julgamento do
            analista.
          </p>
        </div>
        {/* score + risco + meta */}
        <div className="cep-analysis-main-left">
          <div className="cep-analysis-main-text">
            <div className="cep-chip-row">
              {/* chip risco */}
              <span className={cx(riskCfg.badgeClass)}>
                <span className="cep-chip-dot" />
                {riskCfg.label || "Risco não classificado"}
              </span>

              {/* status auto reprovação */}
              {autoReprovadoPeloMotor ? (
                <span className="cep-chip cep-chip--danger">
                  <HiOutlineXCircle className="cep-chip-icon" />
                  Reprovado automaticamente pelo motor
                </span>
              ) : (
                <span className="cep-chip cep-chip--success">
                  <HiOutlineCheckCircle className="cep-chip-icon" />
                  Sem regra de auto reprovação acionada
                </span>
              )}
            </div>

            <p className="cep-analysis-description">
              Resultado consolidado do motor de crédito, considerando as regras
              configuradas (protestos, pendências, cheques, score e tempo de
              empresa) e o histórico da empresa consultada.
            </p>
          </div>
        </div>
      </div>

      {/* observações e regras acionadas */}
      <div className="cep-analysis-footer grid-cols-1 md:grid-cols-2">
        {/* positivas */}
        <div className="cep-analysis-col cep-analysis-col--positive">
          <div className="cep-analysis-col-header">
            <HiOutlineCheckCircle className="cep-analysis-col-icon" />
            <p className="cep-analysis-col-title">Observações positivas</p>
          </div>
          {observacoesPositivas?.length ? (
            <ul className="cep-observations-list cep-observations-list--positive">
              {observacoesPositivas.map((obs, idx) => (
                <li key={idx} className="cep-observations-item">
                  <span className="cep-observations-dot cep-observations-dot--positive" />
                  <span>{obs}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="cep-observations-empty cep-observations-empty--positive">
              Nenhum ponto positivo relevante identificado.
            </p>
          )}
        </div>

        {/* negativas */}
        <div className="cep-analysis-col cep-analysis-col--negative">
          <div className="cep-analysis-col-header">
            <HiOutlineXCircle className="cep-analysis-col-icon" />
            <p className="cep-analysis-col-title">Observações negativas</p>
          </div>
          {observacoesNegativas?.length ? (
            <ul className="cep-observations-list cep-observations-list--negative">
              {observacoesNegativas.map((obs, idx) => {
                const text =
                  typeof obs === "string"
                    ? obs
                    : obs?.label || obs?.descricao || obs?.texto || "";

                const type =
                  typeof obs === "object" && obs?.type
                    ? obs.type
                    : inferTypeFromText(text);

                return (
                  <li key={idx} className="cep-observations-item">
                    <span className="cep-observations-dot cep-observations-dot--negative" />
                    <span className="cep-observations-main">
                      <span>{text}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="cep-observations-empty cep-observations-empty--negative">
              Nenhum fator de risco relevante informado.
            </p>
          )}
        </div>
      </div>

      <div className="cep-analysis-col cep-analysis-col--rules">
        <div className="cep-analysis-col-header">
          <HiOutlineInformationCircle className="cep-analysis-col-icon" />
          <p className="cep-analysis-col-title">Regras do motor acionadas</p>
        </div>

        {regrasMotorAcionadas?.length ? (
          <>
            <p className="cep-rules-summary">
              <HiOutlineSparkles className="cep-rules-summary-icon" />
              <span>
                {regrasMotorAcionadas.length} regra
                {regrasMotorAcionadas.length > 1 ? "s" : ""} do motor foram
                disparadas para este caso.
              </span>
            </p>
            <div className="cep-rule-chips">
              {regrasMotorAcionadas.map((rule, idx) => {
                const Icon = RULE_ICON_MAP[rule] || HiOutlineInformationCircle;
                return (
                  <span key={`${rule}-${idx}`} className="cep-rule-chip">
                    <Icon className="cep-rule-chip-icon" />
                    <span className="cep-rule-chip-dot" />
                    {RULE_LABELS[rule] || rule}
                  </span>
                );
              })}
            </div>
          </>
        ) : (
          <p className="cep-observations-empty cep-observations-empty--rules">
            Nenhuma regra de auto reprovação foi disparada.
          </p>
        )}
      </div>
    </div>
  );
}

// ======================
// Componente principal
// ======================
export default function CreditEnginePage() {
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [cpfCnpjError, setCpfCnpjError] = useState(null);

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [config, setConfig] = useState(null);
  const [alert, setAlert] = useState(null);

  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  const { userPermission } = useApplication();
  const canEdit = ["ADMIN", "POWER_USER"].includes(userPermission);

  const [detailsModal, setDetailsModal] = useState({
    open: false,
    type: null,
    label: "",
    observation: null,
  });

  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axiosInstance.get(
        USERS.GET_USER_QUERY_PERMISSIONS
      );
      return items.data;
    }
  );

  const hasCreditEnginePermission =
    Array.isArray(userQueryPermissions) &&
    userQueryPermissions.some((item) => {
      const url = item?.url || "";
      if (typeof url !== "string") return false;
      return (
        url.includes("motor-credito-pf") || url.includes("motor-credito-pj")
      );
    });

  // Carregar config inicial do motor de crédito
  useEffect(() => {
    let mounted = true;

    async function loadConfig() {
      try {
        setLoadingConfig(true);
        const { data } = await axiosInstance.get(ADMIN.GET_ENGINE_CREDIT);
        if (mounted) {
          setConfig(data?.data || data || null);
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          setAlert({
            type: "error",
            message:
              "Não foi possível carregar as configurações do motor de crédito.",
          });
        }
      } finally {
        if (mounted) setLoadingConfig(false);
      }
    }

    loadConfig();
    return () => {
      mounted = false;
    };
  }, []);

  // Salvar config do motor
  const handleSaveConfig = async (payload) => {
    try {
      setSavingConfig(true);
      setAlert(null);
      const { data } = await axiosInstance.put(
        ADMIN.SAVE_ENGINE_CREDIT,
        payload
      );
      setConfig(data?.data || data || payload);
      setAlert({
        type: "success",
        message: "Configurações do motor de crédito salvas com sucesso.",
      });
      setShowSettingsModal(false);
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message: "Erro ao salvar as configurações. Tente novamente.",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleOpenSettings = () => {
    if (!canEdit) {
      setAlert({
        type: "warning",
        message: "Você não tem permissão para editar o motor de crédito.",
      });
      return;
    }
    setShowSettingsModal(true);
  };

  const handleCpfCnpjChange = (e) => {
    const digits = onlyDigits(e.target.value).slice(0, 14);
    setCpfCnpj(digits);
    if (cpfCnpjError) setCpfCnpjError(null);
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    const result = validateCpfCnpj(cpfCnpj);

    if (!result.ok) {
      setCpfCnpjError(result.message);
      return;
    }

    try {
      setAnalysisLoading(true);
      setAlert(null);
      setAnalysisResult(null);

      const url =
        cpfCnpj.length === 11 ? "motor-credito-pf" : "motor-credito-pj";

      const resp = await axiosInstance.get(
        `${QUERIES.GET_V2_QUERY}/${url}/${cpfCnpj}`
      );

      // tenta achar o objeto de crédito preditivo em níveis diferentes
      const payload =
        resp?.data?.creditoPreditivo ||
        resp?.data?.data?.creditoPreditivo ||
        resp?.data?.data ||
        resp?.data;

      setAnalysisResult(payload);

      setAlert({
        type: "success",
        message: `Análise concluída para este ${result.type}. Veja o resumo ao lado.`,
      });
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message:
          "Ocorreu um erro ao executar a análise. Verifique a configuração do motor ou tente novamente.",
      });
    } finally {
      setAnalysisLoading(false);
    }
  };

  if (!isLoading && !hasCreditEnginePermission) {
    return <ForbiddenQueriesPermission />;
  }

  // Considerar "configurada" se estiver ativa E tiver pelo menos uma regra/threshold preenchida
  const engineConfigured =
    config &&
    config.isActive &&
    (config.autoRejectProtesto ||
      config.autoRejectChequeSemFundo ||
      config.autoRejectPendenciaFinanceira ||
      config.scoreThreshold ||
      config.protestoMinValue ||
      config.chequeMinCount ||
      config.pendenciaMinValue ||
      config.foundationMinDate);

  const handleOpenDetails = (ctx) => {
    setDetailsModal({
      open: true,
      type: ctx?.type || null,
      label: ctx?.label || "Detalhes da análise",
      observation: ctx?.observation || null,
    });
  };

  const handleCloseDetails = () => {
    setDetailsModal((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <div className="credit-engine-page">
        <div className="cep-shell">
          {/* Header */}
          <header className="cep-header">
            <div className="cep-header-top-row">
              <div className="cep-header-chip">
                <HiOutlineSparkles className="cep-header-chip-icon" />
                <span>Motor de Crédito Inteligente</span>
              </div>
              <div className="cep-header-actions">
                {engineConfigured && (
                  <Badge color="success" size="xs" className="cep-header-badge">
                    Motor configurado com regras ativas
                  </Badge>
                )}
                <Button
                  size="sm"
                  onClick={handleOpenSettings}
                  className="cep-settings-button"
                >
                  <div className="flex items-center gap-2">
                    <HiOutlineCog className="cep-settings-button-icon" />
                    <span>Configurar motor</span>
                  </div>
                </Button>
              </div>
            </div>

            <div className="cep-header-main-row">
              <div className="cep-header-main-text">
                <div className="cep-header-icon-wrapper">
                  <HiOutlineShieldCheck className="cep-header-main-icon" />
                </div>
                <div>
                  <h1 className="cep-header-title">Motor de Crédito</h1>
                  <p className="cep-header-subtitle">
                    Centralize as regras de aprovação e reprovação das suas
                    análises de crédito. Configure critérios automáticos e
                    simule o comportamento do motor usando CPF ou CNPJ.
                  </p>
                </div>
              </div>

              <div className="cep-header-meta">
                {!canEdit && (
                  <span className="cep-header-permission">
                    Apenas ADMIN ou POWER_USER podem editar.
                  </span>
                )}
              </div>
            </div>
          </header>

          {/* Bloco principal */}
          <section className="cep-main-card">
            <div className="cep-main-grid">
              {/* Form */}
              <div className="cep-form-block">
                <div className="cep-form-header">
                  <div className="cep-form-icon-wrapper">
                    <HiOutlineShieldCheck className="cep-form-icon" />
                  </div>
                  <div>
                    <h2 className="cep-form-title">
                      Use o motor de crédito nas suas consultas
                    </h2>
                    <p className="cep-form-subtitle">
                      Digite o CPF ou CNPJ do cliente e deixe o motor aplicar
                      automaticamente as regras configuradas (protestos,
                      pendências, cheques sem fundo e score).
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSimulate} className="cep-form-row">
                  <div className="cep-form-field">
                    <label htmlFor="cpfCnpj" className="cep-form-label">
                      CPF/CNPJ
                    </label>
                    <TextInput
                      id="cpfCnpj"
                      type="text"
                      value={formatCpfCnpj(cpfCnpj)}
                      onChange={handleCpfCnpjChange}
                      color={cpfCnpjError ? "failure" : undefined}
                      helperText={
                        cpfCnpjError ? (
                          <span className="cep-form-error-text">
                            {cpfCnpjError}
                          </span>
                        ) : null
                      }
                      disabled={analysisLoading}
                      className="cep-form-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    color="indigo"
                    className="cep-analyze-button"
                    disabled={loadingConfig || analysisLoading}
                  >
                    {analysisLoading ? (
                      <>
                        <Spinner size={"sm"} />
                      </>
                    ) : loadingConfig ? (
                      <Spinner size={"sm"} />
                    ) : (
                      <HiSearch />
                    )}
                  </Button>
                </form>

                {alert && (
                  <div className="cep-alert-wrapper">
                    <Alert
                      color={
                        alert.type === "success"
                          ? "success"
                          : alert.type === "error"
                          ? "failure"
                          : alert.type === "warning"
                          ? "warning"
                          : "info"
                      }
                      onDismiss={() => setAlert(null)}
                    >
                      <span className="text-xs">{alert.message}</span>
                    </Alert>
                  </div>
                )}
              </div>

              {/* Resultado da análise */}
              <div className="cep-result-block">
                {analysisLoading ? (
                  <AnalysisResultSkeleton />
                ) : analysisResult ? (
                  <AnalysisResultCard
                    result={analysisResult}
                    onOpenDetails={handleOpenDetails}
                  />
                ) : (
                  <div className="cep-empty-result">
                    <p className="cep-empty-result-title">
                      Resultado da análise com o motor
                    </p>
                    <p className="cep-empty-result-text">
                      Após executar uma consulta, o resultado consolidado do
                      motor (score, risco, regras disparadas e recomendação)
                      será exibido aqui.
                    </p>
                    <p className="cep-empty-result-hint">
                      A integração pode consumir o retorno da API e popular este
                      card com o parecer de crédito gerado automaticamente.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Configurações aplicadas */}
            {engineConfigured && (
              <div className="cep-config-card">
                <div className="cep-config-header">
                  <p className="cep-config-title">
                    Configurações do motor aplicadas
                  </p>
                  {loadingConfig && (
                    <span className="cep-config-updating">Atualizando...</span>
                  )}
                </div>

                <ul className="cep-config-list">
                  {/* PROTESTOS */}
                  <li className="cep-config-item">
                    <span
                      className={cx(
                        "cep-config-bullet",
                        config.autoRejectProtesto && "is-active"
                      )}
                    />
                    <div>
                      <p className="cep-config-text">
                        <span className="cep-config-label">Protestos:</span>{" "}
                        {config.autoRejectProtesto ? (
                          <>
                            <span className="cep-config-highlight">
                              reprova automaticamente
                            </span>{" "}
                            {formatMoney(config.protestoMinValue)
                              ? `se o valor total de protestos for igual ou superior a ${formatMoney(
                                  config.protestoMinValue
                                )}.`
                              : "se houver qualquer protesto."}
                          </>
                        ) : (
                          <span className="cep-config-muted">
                            não usados como critério automático.
                          </span>
                        )}
                      </p>
                    </div>
                  </li>

                  {/* CHEQUES SEM FUNDO */}
                  <li className="cep-config-item">
                    <span
                      className={cx(
                        "cep-config-bullet",
                        config.autoRejectChequeSemFundo && "is-active"
                      )}
                    />
                    <div>
                      <p className="cep-config-text">
                        <span className="cep-config-label">
                          Cheques sem fundo:
                        </span>{" "}
                        {config.autoRejectChequeSemFundo ? (
                          <>
                            <span className="cep-config-highlight">
                              reprova automaticamente
                            </span>{" "}
                            {config.chequeMinCount &&
                            Number(config.chequeMinCount) > 0
                              ? `a partir de ${config.chequeMinCount} cheque(s) sem fundo.`
                              : "se existir qualquer cheque sem fundo."}
                          </>
                        ) : (
                          <span className="cep-config-muted">
                            não usados como critério automático.
                          </span>
                        )}
                      </p>
                    </div>
                  </li>

                  {/* PENDÊNCIAS FINANCEIRAS */}
                  <li className="cep-config-item">
                    <span
                      className={cx(
                        "cep-config-bullet",
                        config.autoRejectPendenciaFinanceira && "is-active"
                      )}
                    />
                    <div>
                      <p className="cep-config-text">
                        <span className="cep-config-label">
                          Pendências financeiras:
                        </span>{" "}
                        {config.autoRejectPendenciaFinanceira ? (
                          <>
                            <span className="cep-config-highlight">
                              reprova automaticamente
                            </span>{" "}
                            {formatMoney(config.pendenciaMinValue)
                              ? `se o valor total de pendências for igual ou superior a ${formatMoney(
                                  config.pendenciaMinValue
                                )}.`
                              : "se existirem pendências em aberto."}
                          </>
                        ) : (
                          <span className="cep-config-muted">
                            não usadas como critério automático.
                          </span>
                        )}
                      </p>
                    </div>
                  </li>

                  {/* SCORE MÍNIMO */}
                  <li className="cep-config-item">
                    <span
                      className={cx(
                        "cep-config-bullet",
                        config.scoreThreshold && "is-active"
                      )}
                    />
                    <div>
                      <p className="cep-config-text">
                        <span className="cep-config-label">Score mínimo:</span>{" "}
                        {config.scoreThreshold ? (
                          <span className="cep-config-highlight">
                            reprova automaticamente se o score for menor que{" "}
                            {config.scoreThreshold}.
                          </span>
                        ) : (
                          <span className="cep-config-muted">
                            score não está sendo utilizado como critério
                            automático.
                          </span>
                        )}
                      </p>
                    </div>
                  </li>

                  {/* DATA MÍNIMA DE FUNDAÇÃO */}
                  {config.foundationMinDate && (
                    <li className="cep-config-item">
                      <span className="cep-config-bullet is-active" />
                      <div>
                        <p className="cep-config-text">
                          <span className="cep-config-label">
                            Data mínima de fundação:
                          </span>{" "}
                          <span className="cep-config-highlight">
                            reprova automaticamente empresas com data de
                            fundação posterior a{" "}
                            {formatDate(config.foundationMinDate)}.
                          </span>
                        </p>
                      </div>
                    </li>
                  )}
                </ul>

                {config.notes && (
                  <div className="cep-config-notes">
                    <p className="cep-config-notes-title">
                      Observações internas
                    </p>
                    <p className="cep-config-notes-text">{config.notes}</p>
                  </div>
                )}

                <div className="cep-header-legend mt-4">
                  <span className="cep-header-legend-item">
                    <span className="cep-legend-dot cep-legend-dot--auto" />
                    Reprovação automática pelas regras
                  </span>
                  <span className="cep-header-legend-item">
                    <span className="cep-legend-dot cep-legend-dot--manual" />
                    Itens que podem ser avaliados manualmente
                  </span>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* MODAL DE CONFIGURAÇÃO DO MOTOR */}
      <Modal
        show={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        size="6xl"
        dismissible={!savingConfig}
      >
        <Modal.Header>Configuração do motor de crédito</Modal.Header>
        <Modal.Body>
          {loadingConfig && !config ? (
            <div className="cep-modal-loading">Carregando configurações...</div>
          ) : (
            <CreditEngineSettingsModal
              userRole={userPermission}
              initialConfig={config}
              onSave={handleSaveConfig}
              saving={savingConfig}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* MODAL DE DETALHES DAS OBSERVAÇÕES */}
      <Modal show={detailsModal.open} size="lg" onClose={handleCloseDetails}>
        <Modal.Header>{detailsModal.label}</Modal.Header>
        <Modal.Body>
          {/* Exemplo de renderização de protestos / pendências / cheques */}
          {detailsModal.type === "PROTESTOS" && (
            <>
              <p className="mb-2 text-sm text-gray-600">
                Listagem de protestos encontrados para esta consulta.
              </p>
              {Array.isArray(analysisResult?.protestos) &&
              analysisResult.protestos.length ? (
                <div className="overflow-x-auto">
                  <table className="cep-details-table">
                    <thead>
                      <tr>
                        <th>Cartório</th>
                        <th>UF</th>
                        <th>Valor</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisResult.protestos.map((p, idx) => (
                        <tr key={idx}>
                          <td>{p.cartorio || p.cartorioNome || "—"}</td>
                          <td>{p.uf || p.estado || "—"}</td>
                          <td>
                            {typeof p.valor !== "undefined"
                              ? formatMoney(p.valor)
                              : "—"}
                          </td>
                          <td>{p.data || p.dataProtesto || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Nenhuma listagem detalhada de protestos foi encontrada no
                  retorno.
                </p>
              )}
            </>
          )}

          {detailsModal.type === "PENDENCIAS" && (
            <>
              <p className="mb-2 text-sm text-gray-600">
                Pendências financeiras que influenciaram o parecer do motor.
              </p>
              {Array.isArray(analysisResult?.pendenciasFinanceiras) &&
              analysisResult.pendenciasFinanceiras.length ? (
                <div className="overflow-x-auto">
                  <table className="cep-details-table">
                    <thead>
                      <tr>
                        <th>Credor</th>
                        <th>Origem</th>
                        <th>Valor</th>
                        <th>Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisResult.pendenciasFinanceiras.map((p, idx) => (
                        <tr key={idx}>
                          <td>{p.credor || p.empresa || "—"}</td>
                          <td>{p.origem || p.modalidade || "—"}</td>
                          <td>
                            {typeof p.valor !== "undefined"
                              ? formatMoney(p.valor)
                              : "—"}
                          </td>
                          <td>{p.situacao || p.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Nenhum detalhe de pendências financeiras disponível.
                </p>
              )}
            </>
          )}

          {detailsModal.type === "CHEQUES" && (
            <>
              <p className="mb-2 text-sm text-gray-600">
                Cheques sem fundo considerados na análise.
              </p>
              {Array.isArray(analysisResult?.chequesSemFundo) &&
              analysisResult.chequesSemFundo.length ? (
                <div className="overflow-x-auto">
                  <table className="cep-details-table">
                    <thead>
                      <tr>
                        <th>Banco</th>
                        <th>Agência</th>
                        <th>Número</th>
                        <th>Ocorrência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisResult.chequesSemFundo.map((c, idx) => (
                        <tr key={idx}>
                          <td>{c.banco || c.codBanco || "—"}</td>
                          <td>{c.agencia || "—"}</td>
                          <td>{c.numeroCheque || "—"}</td>
                          <td>{c.motivo || c.ocorrencia || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Nenhum detalhe de cheques sem fundo disponível.
                </p>
              )}
            </>
          )}

          {/* fallback genérico */}
          {["PROTESTOS", "PENDENCIAS", "CHEQUES"].includes(
            detailsModal.type
          ) === false && (
            <p className="text-sm text-gray-600">
              {detailsModal.observation
                ? typeof detailsModal.observation === "string"
                  ? detailsModal.observation
                  : JSON.stringify(detailsModal.observation, null, 2)
                : "Nenhum detalhe adicional disponível para esta observação."}
            </p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
